import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import CreateNewFolderOutlinedIcon from "@mui/icons-material/CreateNewFolderOutlined";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import DriveFileMoveOutlinedIcon from "@mui/icons-material/DriveFileMoveOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import LocationCityOutlinedIcon from "@mui/icons-material/LocationCityOutlined";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import { ProjectResponse, type GetProjectResponse } from "api/generatedApi";
import { useCopyFolder } from "components/page/folder/copy/hooks/use-copy";
import { useCreateFolder } from "components/page/folder/create-edit/hooks/create/use-create-folder";
import { useEditFolderName } from "components/page/folder/create-edit/hooks/edit-folder-name/use-edit-folder-name";
import { useDeleteFolder } from "components/page/folder/hooks/use-delete-folder";
import { FolderInfo } from "components/page/folder/info/info";
import { useMoveFolder } from "components/page/folder/move/hooks/use-move";
import { UseTreeFolderCounter } from "components/shared/tree-folder-counter/use-tree-folder-counter";
import { ExtendedProjectFolder } from "pages/project/hooks/use-map-tree-to-flat";
import { Fragment, useRef, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDialog } from "shared/dialog/use-dialog";
import { useToast } from "shared/toast/hooks/use-toast";
import { useFolderRestrictions } from "shared/user-restrictions/use-folder-restrictions";
import { ChooseProject } from "components/page/folder/copy/choose-project";
import { useProjects } from "context/projects/projects-context";

interface ProjectPopupProps {
  project: GetProjectResponse;
  selectedFolder: ExtendedProjectFolder;
  dataFlatlist: ExtendedProjectFolder[];
  anchorEl: HTMLElement | undefined;
  onClose: () => void;
}

export function ProjectPopupMenu(props: ProjectPopupProps) {
  const { project, selectedFolder, dataFlatlist, anchorEl, onClose } = props;
  const { t } = useTranslation();
  const toast = useToast();
  const sourceProject: ProjectResponse = {
    projectId: project.id,
    projectName: project.title || "",
  };
  const { canDeleteFolder, canEditFolder, canCreateFolder, canCopyFolder, canMoveFolder } = useFolderRestrictions(project);
  const { validateCreateFolder } = UseTreeFolderCounter();
  const [openChooseProjectDialog, closeChooseProjectDialog] = useDialog(ChooseProject);
  const [crossProjectCopy, setCrossProjectCopy] = useState(false);
  const projects = useProjects().projects;
  // filter projects to only include those where the user is an owner or assistant owner for copy functionality
  const filteredProjects = projects?.projects?.filter((project) => project.currentUserRole === "ProjectOwner" || project.currentUserRole === "ProjectAssistantOwner") || [];

  const copyParamsRef = useRef({
    sourceProject,
    destinationProject: sourceProject,
    selectedFolder,
    dataFlatlist: dataFlatlist,
    crossProjectCopy: crossProjectCopy,
  });

  useEffect(() => {
    copyParamsRef.current = {
      sourceProject,
      destinationProject: sourceProject,
      selectedFolder,
      dataFlatlist: dataFlatlist,
      crossProjectCopy: false,
    };
  }, [project, selectedFolder]);

  const [openInfoDialog] = useDialog(FolderInfo);

  const openCreateFolderDialog = useCreateFolder({
    project,
    folder: selectedFolder,
  });

  const openEditFolderNameDialog = useEditFolderName({
    project,
    folder: selectedFolder,
  });

  const openMoveFolderDialog = useMoveFolder({ project, selectedFolder, dataFlatlist });

  //This will be created once, but the function it returns will use the current ref values
  const openCopyFolderDialog = useCopyFolder(copyParamsRef);

  const openDeleteFolderDialog = useDeleteFolder({ project, folder: selectedFolder });

  const openCopy = () => {
    if (filteredProjects.length < 2) clickHandler("copy");
    else {
      clickHandler("chooseProject");
    }
  };

  const handleProjectChange = async (project: ProjectResponse, dataFlatlist: ExtendedProjectFolder[], crossProjectCopy: boolean) => {
    if (!project || !dataFlatlist) return;

    setCrossProjectCopy(crossProjectCopy);
    copyParamsRef.current = {
      sourceProject,
      destinationProject: project,
      selectedFolder,
      dataFlatlist: dataFlatlist,
      crossProjectCopy: crossProjectCopy,
    };

    closeChooseProjectDialog();
    clickHandler("copy");
  };

  const clickHandler = (popupMenuId: string) => {
    switch (popupMenuId) {
      case "create":
        openCreateFolderDialog();
        break;
      case "info":
        openInfoDialog({ folder: selectedFolder });
        break;
      case "edit":
        openEditFolderNameDialog();
        break;
      case "link":
        if (selectedFolder) {
          navigator.clipboard.writeText(window.location.origin + "/projects/" + project.id + "/folders/" + selectedFolder.projectFolderId);
          toast.success(t("folder.link.success"));
        }
        break;
      case "move":
        openMoveFolderDialog();
        break;
      case "copy":
        openCopyFolderDialog();

        break;
      case "delete":
        openDeleteFolderDialog();
        break;
      case "chooseProject":
        openChooseProjectDialog({ onSubmit: handleProjectChange, projects: filteredProjects, sourceProject: sourceProject });
        break;
    }
    onClose();
  };

  return (
    <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={onClose}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          p: 2,
          pt: 1.5,
          minWidth: 220,
          maxWidth: 350,
        }}
      >
        <ListItemIcon sx={{ minWidth: "36px" }}>{selectedFolder ? <FolderOutlinedIcon sx={{ color: "text.primary" }} /> : <LocationCityOutlinedIcon sx={{ color: "text.primary" }} />}</ListItemIcon>
        <Typography title={selectedFolder.projectFolderName ? selectedFolder.projectFolderName : project.title ? project.title : ""} noWrap sx={{ fontWeight: "bold" }}>
          {selectedFolder ? selectedFolder.projectFolderName : project.title}
        </Typography>
      </Box>
      <Divider sx={{ mb: 1 }} />
      {selectedFolder && (
        <Fragment>
          {canCreateFolder() && validateCreateFolder(dataFlatlist.find((f) => f.projectFolderId === selectedFolder.projectFolderId)) && (
            <Fragment>
              <MenuItem onClick={() => clickHandler("create")}>
                <ListItemIcon>
                  <CreateNewFolderOutlinedIcon />
                </ListItemIcon>
                <ListItemText>{selectedFolder.isRoot ? t("common.create") : t("folder.menu.create")}</ListItemText>
              </MenuItem>
              <Divider />
            </Fragment>
          )}
          <MenuItem onClick={() => clickHandler("info")}>
            <ListItemIcon>
              <InfoOutlinedIcon />
            </ListItemIcon>
            <ListItemText>{selectedFolder.isRoot ? t("folder.menu.rootInfo") : t("folder.menu.info")}</ListItemText>
          </MenuItem>
          {canEditFolder() && !selectedFolder.isRoot && (
            <MenuItem onClick={() => clickHandler("edit")}>
              <ListItemIcon>
                <EditOutlinedIcon />
              </ListItemIcon>
              <ListItemText>{t("folder.menu.rename")}</ListItemText>
            </MenuItem>
          )}
          <Divider />
          <MenuItem onClick={() => clickHandler("link")}>
            <ListItemIcon>
              <LinkOutlinedIcon />
            </ListItemIcon>
            <ListItemText>{selectedFolder.isRoot ? t("folder.menu.rootLink") : t("folder.menu.link")}</ListItemText>
          </MenuItem>
          {canMoveFolder() && !selectedFolder.isRoot && (
            <MenuItem onClick={() => clickHandler("move")}>
              <ListItemIcon>
                <DriveFileMoveOutlinedIcon />
              </ListItemIcon>
              <ListItemText>{t("folder.menu.move")}</ListItemText>
            </MenuItem>
          )}
          {canCopyFolder() && !selectedFolder.isRoot && (
            <MenuItem onClick={openCopy}>
              <ListItemIcon>
                <ContentCopyOutlinedIcon />
              </ListItemIcon>
              <ListItemText>{t("folder.menu.copy")}</ListItemText>
            </MenuItem>
          )}
        </Fragment>
      )}
      {canDeleteFolder(selectedFolder) && !selectedFolder.isRoot && (
        <Fragment>
          <Divider />
          <MenuItem onClick={() => clickHandler("delete")}>
            <ListItemIcon>
              <DeleteOutlinedIcon />
            </ListItemIcon>
            <ListItemText>{t("folder.menu.delete")}</ListItemText>
          </MenuItem>
        </Fragment>
      )}
    </Menu>
  );
}
